import { useEffect, useId, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { AdminOrganizationList } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageHeading } from '@traceio-org/ui';
import { MAX_SEARCH_LENGTH, ORGANIZATIONS_PAGE_SIZE, normalizeSearch } from '../api';
import { AdminPage } from '../components/AdminPage';
import { ActionLink, AdminProblem } from '../components/AdminProblem';
import { DateText, ProductSummary, ScrollTable, StatusBadge } from '../components/records';
import { useDialogTarget } from '../components/WriteDialog';
import { useAbilities, useAdmin, useStaff } from '../context';
import { formatCount, plural } from '../format';
import { useAdminRead } from '../useAdminRead';
import { NewOrganizationDialog } from './NewOrganizationDialog';

/** The search reaches platform-api this long after the last keystroke (Enter sends it at once). */
export const SEARCH_DEBOUNCE_MS = 300;

/** The query string of a list view: the search and the cursor of the page shown (none for the first). */
export function listSearch(query: string, cursor?: string): string {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (cursor) params.set('cursor', cursor);
  const search = params.toString();
  return search ? `?${search}` : '';
}

/**
 * The cursors of the pages before this one ('' for the first page), kept in the history entry. Only this
 * page writes it; anything else in location.state (or none, after a reload or a shared link) reads as
 * unknown, and the pager then offers the first page instead of the previous one.
 */
export function previousCursors(state: unknown): string[] | null {
  if (typeof state !== 'object' || state === null) return null;
  const previous = (state as { previous?: unknown }).previous;
  if (!Array.isArray(previous) || !previous.every((cursor) => typeof cursor === 'string')) return null;
  return previous as string[];
}

/** What the detail page needs to come back to this exact list view. */
export interface ListReturnState {
  listSearch: string;
}

function describeResults(count: number, query: string, page: number | null, hasNext: boolean): string {
  const organizations = plural(count, 'organization', 'organizations');
  const scope = query ? `${organizations} matching "${query}"` : `Showing ${organizations}`;
  if (page === 1 && !hasNext) return scope;
  return page === null ? scope : `${scope}, page ${formatCount(page)}`;
}

/** The polite live line above the results: what is loading, how many were found, or that none were. */
function statusLine({
  canRead,
  loading,
  current,
  query,
  cursor,
  pageNumber,
}: {
  canRead: boolean;
  loading: boolean;
  current: AdminOrganizationList | null;
  query: string;
  cursor: string | undefined;
  pageNumber: number | null;
}): string {
  // Without the permission the forbidden state says it all; on a failure the alert does.
  if (!canRead) return '';
  if (loading) return 'Loading organizations';
  if (!current) return '';
  if (current.items.length > 0) {
    return describeResults(current.items.length, query, pageNumber, current.nextCursor !== null);
  }
  if (query) return `No organizations match "${query}"`;
  return cursor ? 'No more organizations' : 'No organizations yet';
}

function OrganizationsTable({
  list,
  caption,
  busy,
  returnSearch,
}: {
  list: AdminOrganizationList;
  caption: string;
  busy: boolean;
  returnSearch: string;
}) {
  const returnState: ListReturnState = { listSearch: returnSearch };
  return (
    <ScrollTable caption={caption} busy={busy} className="admin-table admin-table--organizations">
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Slug</th>
          <th scope="col">Status</th>
          <th scope="col">Products</th>
          <th scope="col" className="admin-num">
            Members
          </th>
          <th scope="col">Created</th>
        </tr>
      </thead>
      <tbody>
        {list.items.map((organization) => (
          <tr key={organization.id}>
            <th scope="row">
              <Link to={`/organizations/${organization.id}`} state={returnState}>
                {organization.displayName}
              </Link>
            </th>
            <td>
              <code>{organization.slug}</code>
            </td>
            <td>
              <StatusBadge status={organization.status} />
            </td>
            <td>
              <ProductSummary products={organization.products} />
            </td>
            <td className="admin-num">{formatCount(organization.memberCount)}</td>
            <td>
              <DateText iso={organization.createdAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

function PagerButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  children: ReactNode;
}) {
  // aria-disabled instead of disabled: a button that becomes unavailable after a page change keeps the
  // keyboard focus instead of dropping it on the document.
  const handle = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    onClick();
  };
  return (
    <button type="button" className="admin-action" aria-disabled={disabled || undefined} onClick={handle}>
      {children}
    </button>
  );
}

/**
 * Organizations (/organizations): a server-side search (debounced, in the URL as ?q=) and keyset pagination of 25 per
 * page (?cursor= plus the earlier cursors in the history entry), reading GET /v1/admin/organizations with
 * the admin-audience token. Every row platform-api returns is audited there; the page says so.
 */
export function OrganizationsPage() {
  const { api } = useAdmin();
  const { context } = useStaff();
  const abilities = useAbilities();
  const canRead = context.permissions.includes('staff.orgs.read');
  // New organization: platform admins only (platform-api checks staff.orgs.manage again on save).
  const creation = useDialogTarget<true>();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const query = normalizeSearch(params.get('q') ?? '');
  const cursor = params.get('cursor') || undefined;
  const previous = cursor ? previousCursors(location.state) : [];
  const pageNumber = previous ? previous.length + 1 : null;
  const inputId = useId();
  const [text, setText] = useState(query);

  // Follow the URL when it changes from elsewhere (back and forward, the navigation), keeping what was
  // typed when it already means the same search.
  useEffect(() => {
    setText((current) => (normalizeSearch(current) === query ? current : query));
  }, [query]);

  // The search goes to the URL (and so to platform-api) once typing pauses. It replaces the history entry:
  // a search is a refinement, not a page to come back to.
  useEffect(() => {
    const next = normalizeSearch(text);
    if (next === query) return;
    const timer = window.setTimeout(() => {
      void navigate({ search: listSearch(next) }, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [text, query, navigate]);

  const key = canRead ? `organizations\u0000${query}\u0000${cursor ?? ''}` : null;
  const read = useAdminRead(key, (signal) =>
    api.listOrganizations({ query, ...(cursor ? { cursor } : {}), limit: ORGANIZATIONS_PAGE_SIZE }, signal),
  );
  const current = read.dataKey === key ? read.data : null;
  const shown = read.failure ? null : read.data;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = normalizeSearch(text);
    if (next !== query) void navigate({ search: listSearch(next) }, { replace: true });
  };
  // The earlier cursors are known only when this page was reached by paging; from a pasted or reloaded
  // page link they stay unknown, and the pager offers the first page instead of guessing a number.
  const goNext = (nextCursor: string) =>
    void navigate(
      { search: listSearch(query, nextCursor) },
      { state: previous ? { previous: [...previous, cursor ?? ''] } : null },
    );
  const goPrevious = () => {
    if (!previous || previous.length === 0) return;
    const before = previous[previous.length - 1];
    void navigate(
      { search: listSearch(query, before || undefined) },
      { state: { previous: previous.slice(0, -1) } },
    );
  };
  const goFirst = () => void navigate({ search: listSearch(query) });

  const status = statusLine({ canRead, loading: read.loading, current, query, cursor, pageNumber });

  let results: ReactNode;
  if (!canRead) {
    results = <AdminProblem failure={{ kind: 'forbidden' }} subject="organizations" />;
  } else if (read.failure) {
    results = (
      <AdminProblem
        failure={read.failure}
        subject="organizations"
        onRetry={read.retry}
        action={
          read.failure.kind === 'invalid-request' ? (
            <ActionLink primary to={{ pathname: '/organizations', search: listSearch(query) }}>
              Go to the first page
            </ActionLink>
          ) : undefined
        }
      />
    );
  } else if (shown && shown.items.length > 0) {
    results = (
      <OrganizationsTable
        list={shown}
        busy={read.loading}
        caption={pageNumber ? `Organizations, page ${formatCount(pageNumber)}` : 'Organizations'}
        returnSearch={location.search}
      />
    );
  } else if (current) {
    // Empty for the current search and page (never an earlier, stale answer).
    results = query ? (
      <EmptyPageState
        icon={<NavigationGlyph name="Search" />}
        title={`No organizations match "${query}"`}
        description="Search looks at organization names and slugs."
        action={<ActionLink to="/organizations">Clear the search</ActionLink>}
      />
    ) : cursor ? (
      <EmptyPageState
        icon={<NavigationGlyph name="Building2" />}
        title="No more organizations"
        description="This page is past the end of the list."
        action={<ActionLink to="/organizations">Go to the first page</ActionLink>}
      />
    ) : (
      <EmptyPageState
        icon={<NavigationGlyph name="Building2" />}
        title="No organizations yet"
        description="Organizations appear here once they exist on Trace."
      />
    );
  } else {
    // First load: the status line announces it; this is the visual placeholder.
    results = (
      <div className="admin-loading" aria-hidden="true">
        <span className="admin-spinner" />
        Loading organizations
      </div>
    );
  }

  const showPager = !read.failure && shown !== null && (cursor !== undefined || shown.nextCursor !== null);
  const nextCursor = current?.nextCursor ?? null;

  return (
    <AdminPage title="Organizations">
      <PageHeading
        title="Organizations"
        description="Every organization on Trace. Staff reads are audited: each organization you list or open is recorded with your name and staff role."
        actions={
          abilities.createOrganizations ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => creation.show(true)}
            >
              New organization
            </button>
          ) : undefined
        }
      />
      {abilities.createOrganizations ? (
        <NewOrganizationDialog key={creation.key} open={creation.open} onClose={creation.close} />
      ) : null}
      <div className="admin-list">
        <div className="admin-toolbar">
          <form role="search" className="admin-search" onSubmit={submit}>
            <label htmlFor={inputId} className="admin-sr-only">
              Search organizations
            </label>
            <span className="admin-search__icon" aria-hidden="true">
              <NavigationGlyph name="Search" />
            </span>
            <input
              id={inputId}
              type="search"
              value={text}
              onChange={(event) => setText(event.currentTarget.value)}
              maxLength={MAX_SEARCH_LENGTH}
              placeholder="Search by name or slug"
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="search"
              disabled={!canRead}
            />
          </form>
          <p className="admin-results-status" role="status" aria-live="polite">
            {status}
          </p>
        </div>
        {results}
        {showPager ? (
          <div className="admin-pager">
            {previous ? (
              <PagerButton onClick={goPrevious} disabled={previous.length === 0 || read.loading}>
                Previous page
              </PagerButton>
            ) : (
              <PagerButton onClick={goFirst} disabled={read.loading}>
                First page
              </PagerButton>
            )}
            <PagerButton
              onClick={() => {
                if (nextCursor) goNext(nextCursor);
              }}
              disabled={nextCursor === null || read.loading}
            >
              Next page
            </PagerButton>
          </div>
        ) : null}
      </div>
    </AdminPage>
  );
}
